<template>
    <b-overlay :show="loading" class="animated">
        <batch-result-manager-server-table-actions 
            class="mt-5"
            @toggle="applyToggle"
            @option="applyOption"
        />
        <b-table
            class="mt-3"
            :items="filteredResults"
            :fields="table.fields"
            :per-page="table.per_page"
            v-sortable="table.sorts"
            :current-page="table.current_page"
            id="results-table"
            responsive
        >
            <template #cell(select)="data">
                <span v-if="isSelected(data.item)" @click="toggleSelect(data.item)">
                    <i class="fal fa-check-square text-success fa-2x" />
                </span>
                <span v-else @click="toggleSelect(data.item)">
                    <i class="fal fa-square text-info fa-2x" />
                </span>
            </template>
            <template #cell(sample_id)="data">
                <a :href="`#/samples/${data.item.sample.id}`">
                    {{ data.item.sample.receiving_id }}
                </a>
            </template>
            <template #cell(submission)="data">
                <a :href="getSubmissionHref(data)">
                    {{ displaySubmissionNumber(data) }}
                </a>
            </template>
            <template #cell(created_by)="data">
                {{ data.value.name }}
            </template>
            <template #cell(analyte_id)="data">
                <a :href="`#/analytes/${data.item.analyte.id}`">
                    {{ data.item.analyte.name }}
                </a>
            </template>
            <template #cell(status)="data">
                <toggle-select-button 
                    :status_options="status_options"
                    :default_value="default_select_value(data)"
                    :hide_button="true"
                    :read_only="true"
                />
            </template>
            <template #cell(actions)="data">
                <a
                    class="btn btn-info px-1 py-0 text-white mr-1"
                    :href="`#/result-manager/${data.item.id}`"
                >
                    <i class="fa fa-eye" />
                </a>
                <a
                    class="btn btn-warning px-1 py-0 text-white mr-1"
                    :href="`#/result-manager/${data.item.id}/edit`"
                >
                    <i class="fa fa-pencil" />
                </a>
            </template>
        </b-table>
        <custom-table-pagination
            :options="table.per_page_options"
            :current_page="table.current_page"
            :per_page="table.per_page"
            :total_rows="filteredResults.length"
            table_id="results-table"
            @update="updatePaginationData"
        />
    </b-overlay>
</template>

<script>
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import BatchResultManagerServerTableActions from "./BatchResultManagerServerTableActions";
import sortable from "@/components/shared/directives/Sortable";
import CustomTablePagination from "@/components/shared/CustomTablePagination";
import ToggleSelectButton from "@/components/shared/form/ToggleSelectButton";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

const AnalyteResultRepository = LabRepositoryFactory.get('analyteResult');

export default {
    name: "BathcResultManagerServerTable",
    directives: { sortable },
    mixins: [ ToastNotifications ],
    components: {
        BatchResultManagerServerTableActions,
        CustomTablePagination,
        ToggleSelectButton
    },
    props: {
        collection: {
            type: Object,
        },
        filters: {
            type: Object,
            required: true
        },
        show_columns: {
            type: Array,
            default: () => [
                "select",
                "batch_index",
                "sample_id",
                "submission",
                "created_by",
                "analyte_id",
                "formatted_result",
                "status",
                "actions"
            ]
        },
        loading: {
            type: Boolean,
            required: true
        },
        status_options: {
            type: Array,
            required: true
        },
    },
    data() {
        return {
            selected_rows: [],
            table: {
                fields: [
                    { key: "select", label: "Select" },
                    { key: "batch_index", label: "Index" },
                    { key: "sample_id", label: "Sample" },
                    { key: "submission", label: "Submission" },
                    { key: "created_by", label: "User" },
                    { key: "analyte_id", label: "Analyte" },
                    { key: "formatted_result", label: "Result" },
                    { key: "status", label: "Status" },
                    { key: "actions", label: "Actions" }
                ],
                sorts: {},
                per_page: 50,
                current_page: 1,
                per_page_options: [50, 100, 150, 200],
            },
        };
    },
    computed: {
        filteredResults() {
            return this.collection ? this.collection.models : [];
        },
    },
    watch: {
        filteredResults() {
            if (this.filteredResults.length) {
                this.loading = false
            }
        }
    },
    methods: {
        applyOption(optionValue) {
            // check if we have any selected rows
            if (!this.selected_rows.length) {
                // if we don't display a message saying we need some and return out
                console.log('Nothing selected');
                return;
            }

            let payload = {}
            payload.batch = [];
            this.selected_rows.forEach((element) => {
                payload.batch.push({
                    analyte_id: element.id,
                    status_id: optionValue
                });
            });
            // go through the selected rows and update the status to the selected
            // batch api call
            AnalyteResultRepository.batchUpdate(payload).then((response) => {
                if (response.status !== 200) {
                    console.error('Request failed');
                    return;
                }

                // update the selected rows

                this.selected_rows.forEach((element) => {
                    // get the index of the row of the element we have updated
                    const index = this.collection.models.indexOf(element);
                    // get an instance of the model
                    const model = this.collection.models[index];

                    // reassign the analyte status to the updated status
                    // doesn't matter the index of the payload we use as they will all be assigned the same status
                    model.analyte_result_status_logs[0].analyte_result_status_id = payload.batch[0].status_id;
                    // reassign the row to the updated model
                    this.collection.models[index] = model;

                });

                // flush the selected rows array
                this.selected_rows = [];

                this.quickSuccessToast('Status set successfully', 'The results status has been updated successfully');
            });

        },
        default_select_value(row) {
            return row.item.analyte_result_status_logs[0].analyte_result_status_id;
        },
        getSubmissionHref(r) {
            const submission = r.item.getSample().getSubmission();
            if (submission.id) {
                return `#/submissions/${submission.id}`;
            }
    
            return null;
        },
        displaySubmissionNumber(r) {
            const submission = r.item.getSample().getSubmission();
    
            if (!submission.pid) {
                return '';
            }
    
            return submission.publicNumber();
        },
        updatePaginationData({ field, value }) {
            this.table[field] = value;
        },
        toggleSelect(row, process = '') {
            switch (process) {
                case 'select':
                    if (this.isSelected(row)) {
                        break;
                    }

                    this.selected_rows.push(row);
                    break;
                case 'remove':
                    if (!this.isSelected(row)) {
                        break;
                    }

                    this.selected_rows.splice(this.selected_rows.indexOf(row), 1);
                    break;
                default:
                    if (this.isSelected(row)) {
                        // remove from list
                        this.selected_rows.splice(this.selected_rows.indexOf(row), 1);
                    } else {
                        // push to list
                        this.selected_rows.push(row);
                    break;
                }
            }
        },
        isSelected(row) {
            return this.selected_rows.filter(i => i.id === row.id).length;
        },
        applyToggle(toggleResponse) {
            let counter = 0;
            const page_multiplier = this.table.current_page - 1;

            
            while (toggleResponse.value > counter) {
                const top_border = this.table.per_page * page_multiplier + counter;
                const bottom_border = this.table.per_page * page_multiplier + this.filteredResults.length - 1 - counter;

                this.toggleSelect(this.filteredResults[toggleResponse.side === "top" ? top_border : bottom_border], toggleResponse.process);
                counter++;
            }
        }
    },
};
</script>